"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import { Separator } from "@/components/ui/separator";

interface NumberListItem {
  title: string;
  description: string;
}

interface NumberListProps {
  title?: string;
  subtitle?: string;
  items: NumberListItem[];
}

const NumberList = ({ title, subtitle, items }: NumberListProps) => {
  if (!items || items.length === 0) return null;

  return (
    <section className="py-24 px-6 bg-white">
      <div className="container max-w-4xl mx-auto">
        {/* Header */}
        {(title || subtitle) && (
          <div className="text-center mb-16 space-y-4">
            {title && (
              <BlurFade delay={0.1}>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                  {title}
                </h2>
              </BlurFade>
            )}
            {subtitle && (
              <BlurFade delay={0.15}>
                <p className="text-foreground/60 text-lg max-w-2xl mx-auto">
                  {subtitle}
                </p>
              </BlurFade>
            )}
          </div>
        )}

        {/* Numbered Items */}
        <div className="space-y-0">
          {items.map((item, i) => (
            <React.Fragment key={i}>
              {i > 0 && <Separator className="bg-foreground/5 my-0" />}
              <BlurFade delay={0.15 + i * 0.06}>
                <div className="flex items-start gap-6 md:gap-8 py-8">
                  {/* Number */}
                  <div className="flex-shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-secondary flex items-center justify-center">
                    <span className="text-2xl md:text-3xl font-bold text-foreground/80">
                      {i + 1}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="flex-1 space-y-2 pt-1">
                    <h3 className="text-lg md:text-xl font-bold text-foreground">
                      {item.title}
                    </h3>
                    <p className="text-foreground/60 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </BlurFade>
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NumberList;
